/**
 * Owner-only feature flag: whether non-Lite themes (Classic / Modern / Minimal)
 * are exposed at all. When FALSE (default), the storefront and admin behave as
 * if Lite is the only theme that ever existed:
 *
 *   • Storefront switcher hides every non-Lite option.
 *   • Admin sidebar / panels for non-Lite themes are hidden.
 *   • Visitor theme cookie is ignored.
 *   • `getEffectiveTheme()` synchronously returns "lite" on first render,
 *     eliminating the "Classic flash" before cloudSync hydrates.
 *
 * Flag lives in `app_data.khays_themes_enabled` and is mirrored to
 * localStorage by cloudSync. Only the storefront owner (super_admin) can
 * toggle it from the admin dashboard.
 *
 * Tenants NEVER see non-Lite themes regardless of the flag.
 */
import type { ThemePreset } from "@/stores/storefrontThemeStore";
import { getCurrentTenantId } from "@/lib/tenantContext";

const LS_KEY = "khays_themes_enabled";

/** Sync read — returns false if missing/locked, true only if owner flipped it. */
export function areOtherThemesEnabled(): boolean {
  // Tenants are always locked to Lite — no exception.
  if (getCurrentTenantId()) return false;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw == null) return false;
    const parsed = JSON.parse(raw);
    return parsed === true;
  } catch {
    return false;
  }
}

/** Resolve the theme that should actually render right now. */
export function getEffectiveTheme(
  adminDefault: ThemePreset,
  visitorTheme: ThemePreset | null,
  visitorToggleEnabled: boolean,
): ThemePreset {
  if (!areOtherThemesEnabled()) return "lite";
  if (visitorToggleEnabled && visitorTheme) return visitorTheme;
  return adminDefault;
}
